import React, { useState, useEffect } from 'react';
import { Table, Spin, message } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import RelationChangeLogFilter from './components/RelationChangeLogFilter';
import { useRelationChangeLogColumns } from './components/RelationChangeLogColumns';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {
    GET_RELATION_CHANGE_LOGS,
    GetRelationChangeLogsData,
    GetRelationChangeLogsVariables,
    RelationChangeLogFiltersType,
} from './graphql/queries/getRelationChangeLogs';

const RelationChangeLogPage: React.FC = () => {
    const { t } = useTranslation();
    const columns = useRelationChangeLogColumns();
    const [filters, setFilters] = useState<RelationChangeLogFiltersType>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const { data, loading, error, refetch } = useQuery<
        GetRelationChangeLogsData,
        GetRelationChangeLogsVariables
    >(GET_RELATION_CHANGE_LOGS, {
        variables: {
            page: currentPage,
            size: pageSize,
            filters: filters,
        },
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        refetch({
            page: currentPage,
            size: pageSize,
            filters: filters,
        });
    }, [filters, currentPage, pageSize, refetch]);

    if (error) {
        message.error(t('central.relationLogs.messages.error') + ': ' + error.message);
    }

    const handleFilterChange = (newFilters: RelationChangeLogFiltersType) => {
        setFilters(newFilters);
        setCurrentPage(1);
    };

    const handleReset = () => {
        setFilters({});
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    const logs = data?.getRelationChangeLogs?.content || [];
    const totalElements = data?.getRelationChangeLogs?.totalElements || 0;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '16px' 
            }}>
                <span style={{ 
                    height: '20px', 
                    borderLeft: '4px solid #722ed1', 
                    marginRight: '8px' 
                }}></span>
                <span style={{ 
                    fontSize: '18px', 
                    fontWeight: 600, 
                    color: '#722ed1' 
                }}>
                    {t('central.relationLogs.title')}
                </span>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
                <RelationChangeLogFilter
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onReset={handleReset}
                />
            </div>

            <div>
                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={logs}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        size="small"
                        className="mb-4"
                    />
                    <TablePaginationBar
                        total={totalElements}
                        current={currentPage}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        showQuickJumper={false}
                    />
                </Spin>
            </div>
        </div>
    );
};

export default RelationChangeLogPage;